/// <amd-module name="Build/Settings/general/GeneralSettingsService" />
import { GeneralSettingsData } from "Build/Client/Contracts/DataProvider";
import { ContributionSource } from "Build/Flux/Sources/Contribution";
import { getTFSProject } from "Tfs/Platform/Page";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { publishEvent } from "VSS/Platform/Telemetry";

export interface IGeneralSettingsStoreOptions {
    pageContext: IVssPageContext;
}

export interface IGeneralSettingsService extends IVssService {
    data: IReadonlyObservableValue<GeneralSettingsData>;
    updateBadgesArePrivate: (newVal: boolean) => void;
    updateEnforceSettableVar: (newVal: boolean) => void;
    updateAuditEnforceSettableVar: (newVal: boolean) => void;
    updateJobAuthScope: (newVal: boolean) => void;
    updateJobAuthScopeForReleases: (newVal: boolean) => void;
    updatePublishPipelineMetadata: (newVal: boolean) => void;
    updateEnforceReferencedRepoScopedToken: (newVal: boolean) => void;
    updateDisableClassicPipelineCreation: (newVal: boolean) => void;
    updateDisableClassicBuildPipelineCreation: (newVal: boolean) => void;
    updateDisableClassicReleasePipelineCreation: (newVal: boolean) => void;
    updatePipelineTriggerSettings: (settings : {buildsEnabledForForks?: boolean,
        enforceJobAuthScopeForForks?: boolean,
        enforceNoAccessToSecretsFromForks?: boolean,
        forkProtectionEnabled?: boolean,
        isCommentRequiredForPullRequest?: boolean,
        requireCommentsForNonTeamMemberAndNonContributors?: boolean,
        requireCommentsForNonTeamMembersOnly?: boolean}) => void;
    updateEnableShellTasksArgsSanitizing: (newVal: boolean) => void;
    updateDisableImpliedYAMLCiTrigger: (newVal: boolean) => void;
}

export class GeneralSettingsService extends VssService implements IGeneralSettingsService {
    private _settings: ObservableValue<GeneralSettingsData>;
    private _source: ContributionSource;

    public get data(): IReadonlyObservableValue<GeneralSettingsData> {
        return this._settings;
    }

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initialize();
    }

    public _serviceRestart(pageContext: IVssPageContext): void {
        super._serviceRestart(pageContext);
        this._initialize();
    }

    public _serviceEnd(pageContext: IVssPageContext): any {
        super._serviceEnd(pageContext);
    }

    public updateBadgesArePrivate = async (newVal: boolean) => {
        // Invert the value since the setting is stored as isPublic
        await this._updateSetting(this.c_badgesPublic, !newVal);
    };

    public updateEnforceSettableVar = async (newVal: boolean) => {
        await this._updateSetting(this.c_enforceSettableVar, newVal);
    };

    public updateAuditEnforceSettableVar = async (newVal: boolean) => {
        await this._updateSetting(this.c_auditEnforceSettableVar, newVal);
    };

    public updateJobAuthScope = async (newVal: boolean) => {
        await this._updateSetting(this.c_enforceJobAuthScope, newVal);
    };

    public updateJobAuthScopeForReleases = async (newVal: boolean) => {
        await this._updateSetting(this.c_enforceJobAuthScopeForReleases, newVal);
    };

    public updatePublishPipelineMetadata = async (newVal: boolean) => {
        const routeData: { [key: string]: string } = {
            [this.c_publishPipelineMetadata]: newVal.toString()
        };

        const project = getTFSProject(this.pageContext);
        publishEvent(this.pageContext, "PublishPipelineMetadata", "SettingValueSwitch", { value: newVal, projectId: (project && project.id) || "" });
        await this._update(routeData);
    };

    public updateEnforceReferencedRepoScopedToken = async (newVal: boolean) => {
        await this._updateSetting(this.c_enforceReferencedRepoScopedToken, newVal);
    };

    public updateDisableClassicPipelineCreation = async (newVal: boolean) => {
        await this._updateSetting(this.c_disableClassicPipelineCreation, newVal);
    };

    public updateDisableClassicBuildPipelineCreation = async (newVal: boolean) => {
        await this._updateSetting(this.c_disableClassicBuildPipelineCreation, newVal);
    };

    public updateDisableClassicReleasePipelineCreation = async (newVal: boolean) => {
        await this._updateSetting(this.c_disableClassicReleasePipelineCreation, newVal);
    };

    public updatePipelineTriggerSettings = async (settings: {
        buildsEnabledForForks?: boolean;
        enforceJobAuthScopeForForks?: boolean;
        enforceNoAccessToSecretsFromForks?: boolean;
        forkProtectionEnabled?: boolean;
        isCommentRequiredForPullRequest?: boolean;
        requireCommentsForNonTeamMemberAndNonContributors?: boolean;
        requireCommentsForNonTeamMembersOnly?: boolean;
    }) => {
        const {
            forkProtectionEnabled,
            buildsEnabledForForks,
            enforceJobAuthScopeForForks,
            enforceNoAccessToSecretsFromForks,
            isCommentRequiredForPullRequest,
            requireCommentsForNonTeamMembersOnly,
            requireCommentsForNonTeamMemberAndNonContributors
        } = settings;
        var updates: { [key: string]: string } = {};

        if (forkProtectionEnabled !== undefined) {
            updates[this.c_forkProtectionEnabled] = forkProtectionEnabled.toString();
        }

        if (buildsEnabledForForks !== undefined) {
            updates[this.c_buildsEnabledForForks] = buildsEnabledForForks.toString();
        }

        if (enforceJobAuthScopeForForks !== undefined) {
            updates[this.c_enforceJobAuthScopeForForks] = enforceJobAuthScopeForForks.toString();
        }

        if (enforceNoAccessToSecretsFromForks !== undefined) {
            updates[this.c_enforceNoAccessToSecretsFromForks] = enforceNoAccessToSecretsFromForks.toString();
        }

        if (isCommentRequiredForPullRequest !== undefined) {
            updates[this.c_isCommentRequiredForPullRequest] = isCommentRequiredForPullRequest.toString();
        }

        if (requireCommentsForNonTeamMembersOnly !== undefined) {
            updates[this.c_requireCommentsForNonTeamMembersOnly] = requireCommentsForNonTeamMembersOnly.toString();
        }

        if (requireCommentsForNonTeamMemberAndNonContributors !== undefined) {
            updates[this.c_requireCommentsForNonTeamMemberAndNonContributors] = requireCommentsForNonTeamMemberAndNonContributors.toString();
        }

        await this._updateSettings(updates);
    };

    public updateEnableShellTasksArgsSanitizing = async (newVal: boolean) => {
        await this._updateSetting(this.c_enableShellTasksArgsSanitizing, newVal);
    };

    public updateDisableImpliedYAMLCiTrigger = async (newVal: boolean) => {
        await this._updateSetting(this.c_disableImpliedYAMLCiTrigger, newVal);
    };

    private _updateSettings = async (updates: { [key: string]: string }) => {
        await this._update(updates);
    };


    private _updateSetting = async (key: string, newVal: boolean) => {
        const routeData: { [key: string]: string } = {
            [key]: newVal.toString()
        };

        await this._update(routeData);
    };

    private async _update(routeData: { [key: string]: string } | undefined) {
        const settings = await this._source.getDataAsync<GeneralSettingsData>(this.c_contributionId, routeData, true);

        if (!!settings) {
            this._settings.value = settings;
        }
    }

    public refreshData() {}

    private async _initialize() {
        this._source = new ContributionSource({
            pageContext: this.pageContext
        });

        this._settings = new ObservableValue<GeneralSettingsData>({} as GeneralSettingsData);

        await this._update(undefined);
    }

    readonly c_contributionId: string = "ms.vss-build-web.pipelines-general-settings-data-provider";
    readonly c_badgesPublic: string = "badgesArePublic";
    readonly c_enforceSettableVar: string = "enforceSettableVar";
    readonly c_auditEnforceSettableVar: string = "auditEnforceSettableVar";
    readonly c_enforceJobAuthScope: string = "enforceJobAuthScope";
    readonly c_enforceJobAuthScopeForReleases: string = "enforceJobAuthScopeForReleases";
    readonly c_publishPipelineMetadata: string = "publishPipelineMetadata";
    readonly c_enforceReferencedRepoScopedToken: string = "enforceReferencedRepoScopedToken";
    readonly c_disableClassicPipelineCreation: string = "disableClassicPipelineCreation";
    readonly c_disableClassicBuildPipelineCreation: string = "disableClassicBuildPipelineCreation";
    readonly c_disableClassicReleasePipelineCreation: string = "disableClassicReleasePipelineCreation";
    readonly c_forkProtectionEnabled: string = "forkProtectionEnabled";
    readonly c_buildsEnabledForForks: string = "buildsEnabledForForks";
    readonly c_enforceJobAuthScopeForForks: string = "enforceJobAuthScopeForForks";
    readonly c_enforceNoAccessToSecretsFromForks: string = "enforceNoAccessToSecretsFromForks";
    readonly c_isCommentRequiredForPullRequest: string = "isCommentRequiredForPullRequest";
    readonly c_requireCommentsForNonTeamMembersOnly: string = "requireCommentsForNonTeamMembersOnly";
    readonly c_requireCommentsForNonTeamMemberAndNonContributors: string = "requireCommentsForNonTeamMemberAndNonContributors";
    readonly c_enableShellTasksArgsSanitizing: string = "enableShellTasksArgsSanitizing";
    readonly c_disableImpliedYAMLCiTrigger: string = "disableImpliedYAMLCiTrigger";
}

Services.add("IGeneralSettingsService", { serviceFactory: GeneralSettingsService });
